import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/services/api'
import { parsePaginatedResponse } from '@/utils/pagination'
import { getRowsPerPage } from '@/utils/rowsPerPage'

export interface PricingTier {
  id: number
  slug: string
  name: string
  price_monthly: number
  price_yearly: number
  domain_limit: number
  features: string[]
  highlighted?: boolean
}

export interface Subscription {
  id: number
  tier_id: string
  tier_name: string
  status: 'active' | 'cancelled' | 'past_due' | 'trialing'
  current_period_start: string
  current_period_end: string
  cancel_at_period_end: boolean
  domain_usage: number
  domain_limit: number
}

export interface PaymentRecord {
  id: number
  amount: number
  currency: string
  status: 'paid' | 'pending' | 'failed' | 'refunded'
  description: string
  invoice_url: string | null
  created_at: string
}

interface ApiTier {
  id: number
  slug: string
  name: string
  max_domains: number
  max_blacklist_ips?: number
  price_monthly: number | string
  price_yearly: number | string
  has_dns_monitoring?: boolean
  has_dns_hosting?: boolean
  has_ssl_monitoring?: boolean
  has_blacklist_monitoring?: boolean
  has_team_management?: boolean
}

export interface TierFlags {
  has_dns_hosting: boolean
  has_ssl_monitoring: boolean
  has_blacklist_monitoring: boolean
  has_team_management: boolean
}

function tierFeatures(tier: ApiTier): string[] {
  const features = [`${tier.max_domains} domains`]
  if (tier.has_dns_monitoring) features.push('DNS change monitoring')
  if (tier.has_dns_hosting) features.push('DNS hosting')
  if (tier.has_ssl_monitoring) features.push('SSL monitoring')
  if (tier.has_blacklist_monitoring) features.push('Blacklist monitoring')
  if (tier.has_team_management) features.push('Team management')
  return features
}

function mapTier(tier: ApiTier, index: number): PricingTier {
  return {
    id: tier.id,
    slug: tier.slug,
    name: tier.name,
    price_monthly: Number(tier.price_monthly),
    price_yearly: Number(tier.price_yearly),
    domain_limit: tier.max_domains,
    features: tierFeatures(tier),
    highlighted: index === 1,
  }
}

export const useBillingStore = defineStore('billing', () => {
  const tiers = ref<PricingTier[]>([])
  const rawTiers = ref<ApiTier[]>([])
  const subscription = ref<Subscription | null>(null)
  const payments = ref<PaymentRecord[]>([])
  const loading = ref(false)
  const loadingPayments = ref(false)
  const paymentsPage = ref(1)
  const paymentsTotalPages = ref(1)
  const tierFlags = ref<TierFlags>({
    has_dns_hosting: false,
    has_ssl_monitoring: false,
    has_blacklist_monitoring: true,
    has_team_management: false,
  })

  const currentTierId = computed(() => subscription.value?.tier_id ?? null)
  const canDowngrade = computed(() => {
    if (!subscription.value) return false
    return subscription.value.domain_usage <= subscription.value.domain_limit
  })

  function syncTierFlags(): void {
    const tierId = subscription.value?.tier_id
    const apiTier = rawTiers.value.find((t) => String(t.id) === tierId)
      ?? rawTiers.value.find((t) => t.slug === 'free')

    tierFlags.value = {
      has_dns_hosting: apiTier?.has_dns_hosting ?? false,
      has_ssl_monitoring: apiTier?.has_ssl_monitoring ?? false,
      has_blacklist_monitoring: apiTier?.has_blacklist_monitoring ?? true,
      has_team_management: apiTier?.has_team_management ?? false,
    }
  }

  async function fetchTiers(): Promise<void> {
    loading.value = true
    try {
      const { data } = await apiClient.get<{ data: ApiTier[] }>('/subscriptions/tiers')
      rawTiers.value = data.data
      tiers.value = data.data.map(mapTier)
      syncTierFlags()
    } finally {
      loading.value = false
    }
  }

  async function fetchPublicTiers(): Promise<PricingTier[]> {
    const { data } = await apiClient.get<{ data: ApiTier[] }>('/public/tiers')
    return data.data.map(mapTier)
  }

  async function fetchSubscription(): Promise<void> {
    loading.value = true
    try {
      const { data } = await apiClient.get<{ data: Subscription | null }>('/subscriptions/current')
      subscription.value = data.data
      syncTierFlags()
    } finally {
      loading.value = false
    }
  }

  async function fetchPayments(page = 1): Promise<void> {
    loadingPayments.value = true
    paymentsPage.value = page
    try {
      const { data } = await apiClient.get<{
        data: Array<{
          id: number
          amount: number | string
          currency?: string
          status: string
          invoice_number?: string | null
          paid_at?: string | null
          created_at: string
        }>
        current_page?: number
        last_page?: number
        meta?: { current_page: number; last_page: number }
      }>('/billing/invoices', { params: { page, per_page: getRowsPerPage() } })

      const parsed = 'meta' in data && data.meta
        ? parsePaginatedResponse({ data: data.data, meta: data.meta })
        : parsePaginatedResponse({
            data: data.data,
            current_page: data.current_page ?? page,
            last_page: data.last_page ?? 1,
          })

      payments.value = parsed.items.map((payment) => ({
        id: payment.id,
        amount: Number(payment.amount),
        currency: payment.currency ?? 'USD',
        status: payment.status === 'succeeded' ? 'paid' : (payment.status as PaymentRecord['status']),
        description: payment.invoice_number ? `Invoice ${payment.invoice_number}` : 'Subscription payment',
        invoice_url: `/api/v1/billing/invoices/${payment.id}/pdf`,
        created_at: payment.paid_at ?? payment.created_at,
      }))
      paymentsPage.value = parsed.currentPage
      paymentsTotalPages.value = parsed.lastPage
    } finally {
      loadingPayments.value = false
    }
  }

  async function subscribe(tierId: number, billingInterval: 'monthly' | 'yearly' = 'monthly'): Promise<void> {
    const origin = window.location.origin
    const { data } = await apiClient.post<{ checkout_url: string }>('/subscriptions/subscribe', {
      tier_id: tierId,
      billing_interval: billingInterval,
      success_url: `${origin}/app/billing?checkout=success`,
      cancel_url: `${origin}/app/billing/plans?checkout=cancelled`,
    })
    if (data.checkout_url) {
      window.location.assign(data.checkout_url)
    }
  }

  async function changePlan(tierId: number): Promise<void> {
    if (!subscription.value) {
      await subscribe(tierId)
      return
    }

    const currentIndex = tiers.value.findIndex((t) => String(t.id) === subscription.value?.tier_id)
    const targetIndex = tiers.value.findIndex((t) => t.id === tierId)

    if (targetIndex > currentIndex) {
      await apiClient.post('/subscriptions/upgrade', { tier_id: tierId })
    } else {
      await apiClient.post('/subscriptions/downgrade', { tier_id: tierId })
    }
    await fetchSubscription()
  }

  async function cancelSubscription(): Promise<void> {
    await apiClient.post('/subscriptions/cancel')
    await fetchSubscription()
  }

  async function reactivateSubscription(): Promise<void> {
    await apiClient.post('/subscriptions/reactivate')
    await fetchSubscription()
  }

  return {
    tiers,
    rawTiers,
    subscription,
    payments,
    loading,
    loadingPayments,
    paymentsPage,
    paymentsTotalPages,
    currentTierId,
    canDowngrade,
    tierFlags,
    fetchTiers,
    fetchPublicTiers,
    fetchSubscription,
    fetchPayments,
    subscribe,
    changePlan,
    cancelSubscription,
    reactivateSubscription,
  }
})
